import {
  Box,
  Button,
  Separator,
  Flex,
  Heading,
  HStack,
  Image,
  SimpleGrid,
  Stack,
  Text,
  VStack,
} from '@chakra-ui/react'
import { useParentAppLayout } from '@src/components/sco/AppLayout/AppLayoutContext'
import { AppLayoutToolbar } from '@src/components/sco/AppLayout/Toolbar/AppLayoutToolbar.tsx'
import ComponentCard from '@src/components/sco/LayoutBlocks/ComponentCard'
import LPBlock from '@src/components/sco/LayoutBlocks/LPBlock'
import LPHero from '@src/components/sco/LayoutBlocks/LPHero'
import SectionWrapper from '@src/components/sco/LayoutBlocks/SectionWrapper'
import {
  MemberMatchingLandingVariant,
  variants,
} from '@src/components/sco/Matching/MemberMatchingLanding/variants'
import { UserSearchBuilder } from '@src/components/sco/Matching/UserSearchBuilder'
import FeaturedMemberCard from '@src/components/sco/MatchingLP/components/FeaturedMemberCard'
import HowItWorksCard from '@src/components/sco/MatchingLP/components/HowItWorksCard'
import { useBreakpointContext } from '@src/components/sco/utilities/BreakpointProvider'
import { useActiveContentContext } from '@src/contexts/ActiveContentContext'
import { useUniversalEffect } from '@src/hooks/useUniversalEffect'
import { TrackEvent } from '@src/lib/TrackEvent'
import tracker from '@src/lib/tracker'
import NextImage from 'next/image'
import NextLink from 'next/link'
import React, { useMemo } from 'react'

export function MemberMatchingLanding(props: { variantKey: string }) {
  const activeContentContext = useActiveContentContext()
  const layout = useParentAppLayout()
  const { getBreakpointValue } = useBreakpointContext()

  const variant = useMemo<MemberMatchingLandingVariant>(() => {
    return variants[props.variantKey] || variants.default
  }, [props.variantKey])

  useUniversalEffect(() => {
    activeContentContext.setItem({
      __typename: 'LandingPage',
      title: variant.title1,
      description: variant.title2,
    })
    activeContentContext.setBreadcrumbs([
      {
        title: 'Member Matching',
        href: '/members/mathching',
      },
    ])
    return () => activeContentContext.setItem(null)
  }, [props.variantKey])

  const isNavPanelOpen = layout.getPanel('matching-nav')?.isPanelOpen

  return (
    <Box marginX={'auto'} width={'full'}>
      <AppLayoutToolbar />
      <TrackEvent action={'memberMatchLPViewed'} onlyOnScreen={true} traits={{ variantKey: variant.key }} />
      <Flex align={'center'} justify={'center'} width={'full'}>
        <Stack align={'center'} gap={0} width={'full'}>
          <SectionWrapper bgColor={'colorPalette.panel'}>
            <LPHero description={variant.title2} title={variant.title1}>
              <Box maxWidth={'container.xl'} paddingY={[4, 4, 0]} width={'full'}>
                <UserSearchBuilder emailCaptureMethod={'inline'} />
              </Box>
            </LPHero>
          </SectionWrapper>
          <Separator borderColor={'colorPalette.border/25'} width={'full'} />
          <SectionWrapper bgColor={'colorPalette.panel'} paddingY={0}>
            <LPBlock
              title={'How it works'}
              cards={[
                <SimpleGrid
                  key={'how-it-works'}
                  columns={getBreakpointValue([1, 1, 2, 3, 3])}
                  gap={4}
                  width={'full'}
                >
                  <HowItWorksCard
                    image={require('@public/assets/graphics/matching/matching-step-1.png')}
                    step={1}
                    title={'Tell us what you need'}
                  />
                  <HowItWorksCard
                    image={require('@public/assets/graphics/matching/matching-step-2.png')}
                    step={2}
                    title={'Our AI will match you instantly'}
                  />
                  <HowItWorksCard
                    image={require('@public/assets/graphics/matching/matching-step-3.png')}
                    step={3}
                    title={'Grow Faster, Together'}
                  />
                </SimpleGrid>,
              ]}
            />
          </SectionWrapper>
          {variant.featuredPeople && (
            <SectionWrapper bgColor={'colorPalette.container/50'} paddingY={0}>
              <LPBlock
                title={variant.featuredPeopleHeading}
                cards={[
                  <SimpleGrid
                    key={'featured-people'}
                    gap={4}
                    width={'full'}
                    columns={getBreakpointValue([
                      1,
                      1,
                      2,
                      variant.featuredPeople.length,
                      variant.featuredPeople.length,
                    ])}
                  >
                    {variant.featuredPeople.map((person, idx) => (
                      <FeaturedMemberCard
                        key={idx}
                        category={person.title}
                        name={person.fullName}
                        pictureUrl={person.pictureUrl}
                        tags={person.skills}
                        title={person.bio}
                      />
                    ))}
                  </SimpleGrid>,
                ]}
              />
            </SectionWrapper>
          )}

          <SectionWrapper>
            <VStack align={'center'} gap={10} width={'full'}>
              {variant.alternatingBlocks.map((block, idx) => (
                <ComponentCard
                  key={idx}
                  bgColor={'transparent'}
                  btnCTA={block.cta?.label || "Let's do this"}
                  btnHref={block.cta?.href || '/members/matching/start'}
                  btnVariant={'cta'}
                  cardTitle={block.title}
                  description={block.body}
                  btnProps={{
                    onClick: () => {
                      tracker.memberMatchLPCTAClicked()
                      return true
                    },
                  }}
                  component={
                    <Flex align={'center'} flex={2} justify={'center'}>
                      <Image
                        asChild
                        alt={'Image'}
                        borderRadius={'3xl'}
                        maxHeight={'500px'}
                        overflow={'hidden'}
                        src={block.imageUrl}
                        width={'auto'}
                      >
                        <NextImage {...({} as any)} />
                      </Image>
                    </Flex>
                  }
                  direction={
                    block.imagePlacement == 'right'
                      ? getBreakpointValue(['column-reverse', 'column-reverse', 'column-reverse', 'row'])
                      : getBreakpointValue([
                          'column-reverse',
                          'column-reverse',
                          'column-reverse',
                          'row-reverse',
                        ])
                  }
                />
              ))}
            </VStack>
          </SectionWrapper>
          <SectionWrapper bgColor={'colorPalette.container'}>
            <VStack align={'center'} gap={6} paddingY={10} textAlign={'center'} width={'full'}>
              <Heading size={['xl', 'xl', '2xl', '4xl']}>Looking to join a world-changing startup?</Heading>
              <Text textStyle={'labelXl'}>Join here and start matching in less than a minute.</Text>
              <HStack gap={10} justify={'center'} width={'full'}>
                <Separator />
                <Box marginY={10} width={'fit-content'}>
                  <Stack align={'center'} direction={['column', 'column', 'row']} gap={4} justify={'center'}>
                    <Button asChild size={'lg'} variant={'cta'}>
                      <NextLink href={'/members/matching/start'}>Start Searching for Matches</NextLink>
                    </Button>
                    <Text textStyle={'label2xs'}>or</Text>
                    <Button asChild size={'lg'} variant={'outline'}>
                      <NextLink href={'/members/matching/signup/register'}>
                        Signup as Match Candidate
                      </NextLink>
                    </Button>
                  </Stack>
                </Box>
                <Separator />
              </HStack>
            </VStack>
          </SectionWrapper>
        </Stack>
      </Flex>
    </Box>
  )
}
